import { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  SupervisorAccount as CoordinatorIcon,
  MenuBook as MenuBookIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  History as HistoryIcon,
  SwapHoriz as SwapHorizIcon,
  Help as HelpIcon,
  Star as StarIcon,
  Assignment as AssignmentIcon,
  Timeline as TimelineIcon,
  Groups as GroupsIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

export default function Guide() {
  const { profile } = useAuth();
  const [tabValue, setTabValue] = useState(() => {
    
    if (profile?.rol === 'elev') return 0;
    if (profile?.rol === 'profesor') return 1;
    if (profile?.rol === 'coordonator') return 2;
    return 0;
  });

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  
  const StudentGuide = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Bine ai venit in sistemul de credite scolare! Aici vei gasi toate informatiile necesare pentru a-ti gestiona creditele.
      </Alert>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon color="primary" />
            <Typography fontWeight="bold">Ce sunt creditele scolare?</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>
            Creditele scolare sunt puncte pe care le acumulezi participand la diferite activitati educationale. 
            Exista trei tipuri de credite:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><MenuBookIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Credite Curriculare" 
                secondary="Obtinute din activitati legate de materiile scolare (olimpiade, concursuri scolare, proiecte la materii)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><StarIcon color="warning" /></ListItemIcon>
              <ListItemText 
                primary="Credite Extracurriculare" 
                secondary="Obtinute din activitati extra-scolare (voluntariat, proiecte personale, cursuri externe)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><SwapHorizIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Credite Transferabile" 
                secondary="Credite care pot fi transferate intre module sau ani scolari"
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            <Typography fontWeight="bold">Cum depun o cerere de credite?</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>Pentru a depune o cerere de credite, urmeaza acesti pasi:</Typography>
          <List>
            <ListItem>
              <ListItemIcon><Chip label="1" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Acceseaza 'Cerere Noua' din meniul principal" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="2" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Selecteaza tipul de activitate din lista predefinita sau descrie activitatea" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="3" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Completeaza descrierea detaliata a activitatii" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="4" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Incarca dovezile (diplome, certificate, fotografii)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="5" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Trimite cererea pentru validare" />
            </ListItem>
          </List>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Asigura-te ca ai dovezi clare pentru activitatile declarate. Cererile fara dovezi pot fi respinse.
          </Alert>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="primary" />
            <Typography fontWeight="bold">Cum vad statusul cererilor mele?</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>
            Accesand "Cererile Mele" din meniu vei vedea toate cererile tale si statusul lor:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%' }}>
            <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(33.333% - 16px)' }, display: 'flex' }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 120 }}>
                <Typography variant="body2" fontWeight="bold" color="warning.main">In asteptare</Typography>
                <Typography variant="caption">Cererea a fost trimisa si asteapta validare</Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(33.333% - 16px)' }, display: 'flex' }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 120 }}>
                <Typography variant="body2" fontWeight="bold" color="success.main">Validat</Typography>
                <Typography variant="caption">Cererea a fost aprobata, creditele au fost acordate</Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: { xs: '0 0 100%', sm: '0 0 calc(33.333% - 16px)' }, display: 'flex' }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 120 }}>
                <Typography variant="body2" fontWeight="bold" color="error.main">Respins</Typography>
                <Typography variant="caption">Cererea a fost respinsa (vezi motivul)</Typography>
              </Paper>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon color="primary" />
            <Typography fontWeight="bold">Ce sunt modulele (MOD1-MOD5)?</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>
            Creditele sunt organizate pe 5 module tematice. Fiecare modul reprezinta un domeniu de competente:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><Chip label="MOD1" size="small" /></ListItemIcon>
              <ListItemText primary="Modul 1 - Competente academice" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="MOD2" size="small" /></ListItemIcon>
              <ListItemText primary="Modul 2 - Competente sociale" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="MOD3" size="small" /></ListItemIcon>
              <ListItemText primary="Modul 3 - Competente de dezvoltare personala" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="MOD4" size="small" /></ListItemIcon>
              <ListItemText primary="Modul 4 - Competente civice" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="MOD5" size="small" /></ListItemIcon>
              <ListItemText primary="Modul 5 - Competente profesionale" />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Poti vedea creditele tale pe fiecare modul in profilul tau personal.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon color="primary" />
            <Typography fontWeight="bold">Ce se intampla cu creditele la sfarsitul anului?</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>
            La promovarea in anul urmator:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Creditele sunt arhivate" 
                secondary="Creditele din anul precedent sunt salvate in istoric"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Incepi cu 0 credite" 
                secondary="In noul an scolar pornesti de la zero"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText 
                primary="Poti vedea istoricul" 
                secondary="In profilul tau poti vedea toate creditele din anii anteriori"
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  
  const TeacherGuide = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Ca profesor validator, ai responsabilitatea de a verifica si valida cererile de credite ale elevilor tai.
      </Alert>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupsIcon color="primary" />
            <Typography fontWeight="bold">Cum vad elevii mei?</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>
            Accesand "Elevii Mei" din meniu vei vedea toti elevii pentru care esti desemnat validator.
            Poti vedea:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Numarul total de credite al fiecarui elev" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Distributia creditelor pe categorii" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Cererile in asteptare pentru fiecare elev" />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon color="primary" />
            <Typography fontWeight="bold">Cum validez o cerere?</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>Pentru a valida o cerere:</Typography>
          <List>
            <ListItem>
              <ListItemIcon><Chip label="1" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Acceseaza 'Validare Cereri' din meniu" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="2" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Selecteaza o cerere din lista pentru a vedea detaliile" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="3" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Verifica dovezile atasate de elev" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="4" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Ajusteaza numarul de credite acordate (daca este necesar)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="5" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Aproba sau respinge cererea cu un comentariu" />
            </ListItem>
          </List>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Pentru respingere, este obligatoriu sa specifici motivul. Elevul va fi notificat automat.
          </Alert>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="primary" />
            <Typography fontWeight="bold">Validare in masa</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>
            Poti valida mai multe cereri simultan:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Chip label="1" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Selecteaza cererile folosind checkbox-urile" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="2" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Apasa butonul 'Aproba Selectate'" />
            </ListItem>
            <ListItem>
              <ListItemIcon><Chip label="3" size="small" color="primary" /></ListItemIcon>
              <ListItemText primary="Confirma operatiunea" />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Aceasta functie este utila pentru aprobarea rapida a mai multor cereri similare.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  
  const CoordinatorGuide = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Ca coordonator, ai acces complet la gestionarea sistemului de credite scolare.
      </Alert>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChartIcon color="primary" />
            <Typography fontWeight="bold">Elevi si Statistici</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>
            In sectiunea "Elevi & Statistici" ai acces la:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Lista completa a elevilor" 
                secondary="Cu filtrare dupa clasa, specializare, numar credite"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Grafice si statistici" 
                secondary="Distributia creditelor pe clase, categorii, top elevi"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Export date" 
                secondary="Export in format CSV, Excel sau PDF"
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon color="primary" />
            <Typography fontWeight="bold">Gestionare Clase (Promovare)</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>
            In sectiunea "Mutare Clasa" poti:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Muta individual un elev" 
                secondary="Schimba clasa unui elev cu inregistrare in istoric"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Promovare in masa" 
                secondary="Selecteaza mai multi elevi si promoveaza-i simultan in anul urmator"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Vizualizeaza istoricul" 
                secondary="Vezi toate schimbarile de clasa pentru fiecare elev"
              />
            </ListItem>
          </List>
          <Alert severity="warning" sx={{ mt: 2 }}>
            La promovare, creditele sunt arhivate automat si elevii incep noul an cu 0 credite.
          </Alert>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography fontWeight="bold">Jurnal Audit</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>
            Jurnalul de audit inregistreaza toate actiunile importante din sistem:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Aprobari si respingeri de cereri" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Schimbari de clase ale elevilor" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Modificari de profil" />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Poti filtra dupa tipul actiunii, entitate sau perioada.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon color="primary" />
            <Typography fontWeight="bold">Validare Cereri</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography paragraph>
            Ca si coordonator, poti valida orice cerere din sistem, nu doar cele ale elevilor tai.
            Ai acces la aceleasi functii ca profesorii, plus:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Validare cereri de la orice elev" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Suprascrierea deciziilor anterioare (daca este necesar)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Accesul la toate tipurile de credite" />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
            <HelpIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Ghid de Utilizare
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Tot ce trebuie sa stii despre sistemul de credite scolare
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs pentru roluri - doar pentru profesori si coordonatori */}
      <Card sx={{ borderRadius: 3 }}>
        {profile?.rol !== 'elev' && (
          <Tabs 
            value={tabValue} 
            onChange={(e, v) => setTabValue(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              icon={<PersonIcon />} 
              label="Ghid Elev" 
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab 
              icon={<SchoolIcon />} 
              label="Ghid Profesor" 
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab 
              icon={<CoordinatorIcon />} 
              label="Ghid Coordonator" 
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
          </Tabs>
        )}
        
        <CardContent>
          {profile?.rol === 'elev' ? (
            <StudentGuide />
          ) : (
            <>
              <TabPanel value={tabValue} index={0}>
                <StudentGuide />
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                <TeacherGuide />
              </TabPanel>
              <TabPanel value={tabValue} index={2}>
                <CoordinatorGuide />
              </TabPanel>
            </>
          )}
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card sx={{ mt: 3, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpIcon color="primary" />
            Intrebari Frecvente
          </Typography>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="medium">De ce nu vad creditele mele?</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>
                Creditele apar doar dupa ce cererile tale sunt validate de un profesor. 
                Verifica statusul cererilor in sectiunea "Cererile Mele".
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="medium">Pot modifica o cerere dupa ce am trimis-o?</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>
                Nu, o cerere trimisa nu poate fi modificata. Daca ai gresit, asteapta ca cererea sa fie respinsa 
                sau contacteaza profesorul tau pentru a o respinge, apoi trimite o cerere noua.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="medium">Cat dureaza validarea unei cereri?</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>
                Durata depinde de profesorul validator. In mod normal, cererile sunt procesate in cateva zile. 
                Vei primi o notificare cand cererea ta este procesata.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="medium">Ce fac daca cererea mea a fost respinsa?</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>
                Verifica motivul respingerii in detaliile cererii. Daca consideri ca respingerea este nemeritata, 
                contacteaza profesorul tau sau coordonatorul pentru clarificari. Poti depune o noua cerere cu 
                informatii sau dovezi suplimentare.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    </Container>
  );
}
